import { useMutation } from '@apollo/client';
import { transformError } from '@core/adapters/errors/transformError';
import { track } from '@core/logging';
import { useMemo, useCallback } from 'react';

import { type CreateAuditSessionResponse, type CreateAuditSessionVariables } from '../domain/graphql-types';
import { CREATE_AUDIT_SESSION } from '../graphql/mutations/createAuditSession';

/**
 * Hook for creating audit sessions
 * Enhanced with adapter pattern for better error handling and separation of concerns
 * Includes strategic business event tracking for observability compliance
 */
/* eslint-disable complexity */
export function useCreateAuditSession() {
  const [createAuditSessionMutation, { data, loading, error }] = useMutation<CreateAuditSessionResponse, CreateAuditSessionVariables>(CREATE_AUDIT_SESSION, {
    errorPolicy: 'all',
  });

  // Use GraphQL data directly
  const createdSession = useMemo(() => {
    if (!data?.createAuditSession) {
      return undefined;
    }

    return data.createAuditSession;
  }, [data]);

  // Transform error using simplified adapter pattern
  const domainError = useMemo(() => {
    if (!error) return undefined;

    // Map GraphQL errors to user-friendly format
    const mappedError = transformError(error);

    // Return error for UI handling
    return mappedError;
  }, [error]);

  const createAuditSession = useCallback(
    async (variables: CreateAuditSessionVariables) => {
      const { input } = variables;

      // Track audit session creation initiation
      track('CreateAuditSessionInitiated', {
        communityId: input.sampleParameters.communityId,
        sessionName: input.name,
        hasDescription: Boolean(input.description).toString(),
        hasFilters: Boolean(
          input.sampleParameters.filters.languages?.length ||
            input.sampleParameters.filters.topics?.length ||
            input.sampleParameters.filters.labels?.length ||
            input.sampleParameters.filters.labelSeverities?.length
        ).toString(),
      });

      try {
        const result = await createAuditSessionMutation({ variables });

        // Check for success: data exists and no errors
        if (result.data?.createAuditSession && !result.errors) {
          const sessionData = result.data.createAuditSession;

          // Track successful audit session creation
          track('CreateAuditSessionSucceeded', {
            communityId: input.sampleParameters.communityId,
            sessionId: sessionData.id,
            sessionName: input.name,
            itemsCount: sessionData.itemsCount?.toString() || '0',
          });

          return {
            success: true,
            data: sessionData,
          };
        }

        // Handle errors - normalize to always use errors array
        const errors = result.errors || [];
        const mappedErrors = errors.map(transformError);
        const firstError = mappedErrors[0];

        // Track audit session creation failure
        track('CreateAuditSessionFailed', {
          communityId: input.sampleParameters.communityId,
          sessionName: input.name,
          errorType: 'api_error',
          errorMessage: firstError?.message || 'Unknown error',
        });

        return {
          success: false,
          errors: mappedErrors,
        };
      } catch (error) {
        // Track unexpected audit session creation failure
        track('CreateAuditSessionFailed', {
          communityId: input.sampleParameters.communityId,
          sessionName: input.name,
          errorType: error instanceof Error ? error.name : 'unknown',
          errorMessage: error instanceof Error ? error.message : 'Unexpected error',
        });

        throw error;
      }
    },
    [createAuditSessionMutation]
  );

  return {
    createAuditSession,
    createdSession,
    loading,
    error: domainError,
  };
}
